// 搭乘效果處理器
// Boarding Effect Handlers - 搭乘、上車、下車（小旅人系統）

import type { GameState, Card, BattlefieldPosition, BattlefieldSlot } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('BoardingHandlers');

/**
 * 找到戰場上的卡牌
 */
function findCardOnBattlefield(state: GameState, cardId: string): { card: Card; position: BattlefieldPosition } | null {
  const sides = [
    { side: state.battlefield.player1Side, _sideKey: 'player1' as const },
    { side: state.battlefield.player2Side, _sideKey: 'player2' as const },
  ];

  for (const { side } of sides) {
    for (const slot of [...side.frontLine, ...side.backLine]) {
      if (slot.unit?.id === cardId) {
        return { card: slot.unit, position: slot.position };
      }
    }
  }
  return null;
}

/**
 * 更新戰場上的卡牌
 */
function updateCardOnBattlefield(state: GameState, cardId: string, updater: (card: Card) => Card): GameState {
  const mapSlot = (slot: BattlefieldSlot) =>
    slot.unit?.id === cardId ? { ...slot, unit: updater(slot.unit) } : slot;

  return {
    ...state,
    battlefield: {
      player1Side: {
        frontLine: state.battlefield.player1Side.frontLine.map(mapSlot),
        backLine: state.battlefield.player1Side.backLine.map(mapSlot),
      },
      player2Side: {
        frontLine: state.battlefield.player2Side.frontLine.map(mapSlot),
        backLine: state.battlefield.player2Side.backLine.map(mapSlot),
      },
    },
  };
}

/**
 * 獲取玩家 Key
 */
function getPlayerKey(state: GameState, playerId?: string): 'player1' | 'player2' {
  if (playerId) {
    return playerId === state.player1.id ? 'player1' : 'player2';
  }
  return state.activePlayerId === state.player1.id ? 'player1' : 'player2';
}

/**
 * 搭乘處理器
 * 處理「從森林拿取小旅人放置到單位下方」
 */
export class BoardHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('搭乘') ||
           (text.includes('森林') && text.includes('小旅人') && text.includes('放置'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「從森林拿取1個小旅人搭乘此單位」
    const boardMatch = effectText.match(/(?:從森林)?(?:拿取)?(\d*)(?:個|張)?小旅人(?:搭乘|放置到)(.+)/);
    if (boardMatch) {
      const count = boardMatch[1] ? parseInt(boardMatch[1], 10) : 1;
      const targetIsSelf = effectText.includes('此單位') || effectText.includes('這張');

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'board',
        value: count,
        requiresChoice: !targetIsSelf, // 如果是「此單位」則不需選擇
        targetSelector: targetIsSelf ? undefined : {
          zone: 'battlefield',
          side: 'self',
        },
      });
    }

    // 簡單的「搭乘」關鍵字
    if (effectText.trim() === '搭乘' || effectText.match(/^搭乘[：:]/)) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'board',
        value: 1,
        requiresChoice: false,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'board') {
      return state;
    }

    const targetId = action.targetCardId || action.sourceCardId;
    const count = typeof action.value === 'number' ? action.value : 1;

    if (!targetId) {
      log.debug('缺少目標卡牌 ID');
      return state;
    }

    const targetInfo = findCardOnBattlefield(state, targetId);
    if (!targetInfo) {
      log.debug('找不到目標卡牌');
      return state;
    }

    // 獲取玩家的森林
    const playerKey = getPlayerKey(state, targetInfo.card.ownerId);
    const player = state[playerKey];
    const forest = player.forest;

    // 找到小旅人
    const travelers = forest.filter(c => 
      c.definitionId.includes('traveler') || 
      c.definitionId.includes('小旅人') ||
      c.definitionId.includes('SPT')
    );

    if (travelers.length === 0) {
      log.debug('森林中沒有小旅人');
      return state;
    }

    // 取出指定數量的小旅人
    const toBoard = travelers.slice(0, count);
    const remainingForest = forest.filter(c => !toBoard.some(t => t.id === c.id));

    // 更新森林
    let newState: GameState = {
      ...state,
      [playerKey]: {
        ...player,
        forest: remainingForest,
      },
    };

    // 將小旅人加入目標的 passengers
    newState = updateCardOnBattlefield(newState, targetId, (card) => ({
      ...card,
      passengers: [...(card.passengers || []), ...toBoard],
    }));

    log.debug(`${String(toBoard.length)} 個小旅人搭乘到 ${targetId}`);

    return newState;
  }
}

/**
 * 下車處理器
 * 處理「將小旅人放回森林」
 */
export class DisembarkHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('下車') ||
           (text.includes('小旅人') && text.includes('放回') && text.includes('森林')) ||
           text.includes('下船');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使搭乘的小旅人下車」
    const disembarkMatch = effectText.match(/(?:使)?(?:搭乘的)?(\d*)(?:個|張)?小旅人(?:下車|放回森林)/);
    if (disembarkMatch) {
      const count = disembarkMatch[1] ? parseInt(disembarkMatch[1], 10) : -1; // -1 表示全部

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'disembark',
        value: count,
        requiresChoice: false,
      });
    }

    // 簡單的「下車」
    if (effectText.trim() === '下車') {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'disembark',
        value: -1, // 全部下車
        requiresChoice: false,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'disembark') {
      return state;
    }

    const sourceId = action.sourceCardId;
    const count = typeof action.value === 'number' ? action.value : -1;

    if (!sourceId) {
      log.debug('缺少來源卡牌 ID');
      return state;
    }

    const sourceInfo = findCardOnBattlefield(state, sourceId);
    if (!sourceInfo) {
      log.debug('找不到來源卡牌');
      return state;
    }

    const passengers = sourceInfo.card.passengers || [];
    if (passengers.length === 0) {
      log.debug('沒有搭乘的小旅人');
      return state;
    }

    // 決定要下車的數量
    const disembarkCount = count === -1 ? passengers.length : Math.min(count, passengers.length);
    const toDisembark = passengers.slice(0, disembarkCount);
    const remainingPassengers = passengers.slice(disembarkCount);

    // 獲取玩家
    const playerKey = getPlayerKey(state, sourceInfo.card.ownerId);
    // const _player = state[playerKey]; // 未使用

    // 更新單位的 passengers
    let newState = updateCardOnBattlefield(state, sourceId, (card) => ({
      ...card,
      passengers: remainingPassengers.length > 0 ? remainingPassengers : undefined,
    }));

    // 將小旅人放回森林
    newState = {
      ...newState,
      [playerKey]: {
        ...newState[playerKey],
        forest: [...(newState[playerKey].forest), ...toDisembark.map(c => ({
          ...c,
          location: 'forest' as const,
        }))],
      },
    };

    log.debug(`${String(toDisembark.length)} 個小旅人從 ${sourceId} 下車回到森林`);

    return newState;
  }
}

/**
 * 上車處理器
 * 處理「將特定位置的小旅人放到目標單位下方」
 */
export class EmbarkHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('上車') ||
           text.includes('登船') ||
           (text.includes('小旅人') && text.includes('移動到') && text.includes('下方'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使1個小旅人上車到目標單位」
    const embarkMatch = effectText.match(/(?:使)?(\d*)(?:個|張)?小旅人(?:上車|登船|移動)到(.+)/);
    if (embarkMatch) {
      const count = embarkMatch[1] ? parseInt(embarkMatch[1], 10) : 1;

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'embark',
        value: count,
        requiresChoice: true,
        // 需要先選擇小旅人來源，再選擇目標單位
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'embark') {
      return state;
    }

    const sourceId = action.sourceCardId; // 小旅人來源單位
    const targetId = action.targetCardId;     // 目標單位
    const count = typeof action.value === 'number' ? action.value : 1;

    if (!sourceId || !targetId) {
      log.debug('缺少來源或目標卡牌 ID');
      return state;
    }

    const sourceInfo = findCardOnBattlefield(state, sourceId);
    const targetInfo = findCardOnBattlefield(state, targetId);

    if (!sourceInfo || !targetInfo) {
      log.debug('找不到來源或目標卡牌');
      return state;
    }

    const passengers = sourceInfo.card.passengers || [];
    if (passengers.length === 0) {
      log.debug('來源沒有搭乘的小旅人');
      return state;
    }

    // 移動小旅人
    const toMove = passengers.slice(0, count);
    const remainingPassengers = passengers.slice(count);

    // 更新來源的 passengers
    let newState = updateCardOnBattlefield(state, sourceId, (card) => ({
      ...card,
      passengers: remainingPassengers.length > 0 ? remainingPassengers : undefined,
    }));

    // 更新目標的 passengers
    newState = updateCardOnBattlefield(newState, targetId, (card) => ({
      ...card,
      passengers: [...(card.passengers || []), ...toMove],
    }));

    log.debug(`${String(toMove.length)} 個小旅人從 ${sourceId} 上車到 ${targetId}`);

    return newState;
  }
}

/**
 * 召喚小旅人處理器（增強版）
 * 處理「從額外區召喚小旅人到森林」
 */
export class SummonTravelerToForestHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('召喚') && text.includes('小旅人') && text.includes('森林')) ||
           (text.includes('生成') && text.includes('小旅人'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「召喚1個小旅人到森林」
    const summonMatch = effectText.match(/(?:召喚|生成)(\d*)(?:個|張)?小旅人(?:到森林)?/);
    if (summonMatch) {
      const count = summonMatch[1] ? parseInt(summonMatch[1], 10) : 1;

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'summonTravelerToForest',
        value: count,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'summonTravelerToForest') {
      return state;
    }

    const count = typeof action.value === 'number' ? action.value : 1;
    const playerKey = getPlayerKey(state);
    const player = state[playerKey];

    // 從額外牌組獲取小旅人
    const extraDeck = player.extraDeck || [];
    const travelers = extraDeck.filter(c =>
      c.definitionId.includes('traveler') ||
      c.definitionId.includes('小旅人') ||
      c.definitionId.includes('SPT')
    );

    if (travelers.length === 0) {
      log.debug('額外區沒有小旅人');
      return state;
    }

    const toSummon = travelers.slice(0, count);
    const remainingExtra = extraDeck.filter(c => !toSummon.some(t => t.id === c.id));

    // 更新狀態
    const newState: GameState = {
      ...state,
      [playerKey]: {
        ...player,
        extraDeck: remainingExtra,
        forest: [...(player.forest), ...toSummon.map(c => ({
          ...c,
          location: 'forest' as const,
        }))],
      },
    };

    log.debug(`召喚了 ${String(toSummon.length)} 個小旅人到森林`);

    return newState;
  }
}
